import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Business,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Search,
} from "@mui/icons-material";
import { LogoutIcon, QuestionMarkCircleIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../../app/providers/AuthProvider";
import { APP_SHELL } from "../../../layouts/app/components/sidebar/MenuContent";
import logoMainDark123Zap from "../../../shared/assets/brand/logo-main-dark-123zap.png";
import {
  AccountMenuHeader,
  ACCOUNT_MENU_ITEM_SX,
  ACCOUNT_MENU_LIST_SX,
  ACCOUNT_MENU_PAPER_SX,
  ACCOUNT_MENU_WIDTH,
} from "../../../shared/components/product";
import { ActionMenu } from "../../../shared/components/ui";
import { buildUserAvatarUrl } from "../../../shared/lib/userAvatar";
import { authRoutes } from "../config/authRoutes";
import { useAccountSelection } from "../hooks/useAccountSelection";

function getInitials(name) {
  return String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function formatDocument(account) {
  const documentNumber = String(account.documentNumber || "").trim();

  if (!documentNumber) return "";

  const digits = documentNumber.replace(/\D/g, "");
  let formattedDocument = documentNumber;

  if (digits.length === 11) {
    formattedDocument = digits.replace(
      /(\d{3})(\d{3})(\d{3})(\d{2})/,
      "$1.$2.$3-$4"
    );
  } else if (digits.length === 14) {
    formattedDocument = digits.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      "$1.$2.$3/$4-$5"
    );
  }

  return formattedDocument;
}

function getAccountDescription(account) {
  const documentLabel = formatDocument(account);

  const accountDetails = [documentLabel].filter(Boolean);

  if (accountDetails.length) {
    return accountDetails.join(" · ");
  }

  return "Finalize os dados cadastrais para continuar.";
}

function getAccountDisplayName(account) {
  if (account.legalName || account.documentNumber) {
    return account.name;
  }

  return "Em configuração";
}

function getAccountInitialsSource(account) {
  if (account.legalName || account.documentNumber) {
    return account.name;
  }

  return account.name || "Conta";
}

function openDocumentation() {
  window.open("https://123zap.gitbook.io/docs", "_blank", "noopener,noreferrer");
}

const accessTransitionOptions = [
  {
    id: "signal-scan",
    label: "Signal Scan",
  },
  {
    id: "orbit-pulse",
    label: "Orbit Pulse",
  },
  {
    id: "three-dots-sync",
    label: "Three Dots Sync",
  },
  {
    id: "access-beam",
    label: "Access Beam",
  },
  {
    id: "logo-breath",
    label: "Logo Breath",
  },
];

function resolveAccessTransitionOption(variant) {
  return (
    accessTransitionOptions.find((option) => option.id === variant) ||
    accessTransitionOptions[0]
  );
}

function TopbarIconAction({
  children,
  label,
  onClick,
  controls,
  expanded,
  hasPopup,
  wide = false,
}) {
  return (
    <Tooltip title={label}>
      <ButtonBase
        aria-controls={controls}
        aria-expanded={expanded}
        aria-haspopup={hasPopup}
        aria-label={label}
        onClick={onClick}
        type="button"
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          color: theme.palette.text.secondary,
          display: "flex",
          height: 36,
          justifyContent: "center",
          minWidth: wide ? 72 : 40,
          px: wide ? 1.5 : 0,
          "&:hover": {
            bgcolor: theme.palette.action.hover,
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
          },
          "&:focus-visible": {
            boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
            outline: "none",
          },
        })}
      >
        {children}
      </ButtonBase>
    </Tooltip>
  );
}

export default function SelectAccountPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCardId, setSelectedCardId] = useState("");
  const {
    accounts,
    accountsLabel,
    error,
    hasAccounts,
    loading,
    refreshing,
    reload,
    selectAccount,
    selectedAccountId: loadingAccountId,
  } = useAccountSelection();

  const filteredAccounts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) return accounts;

    return accounts.filter((account) => {
      const searchableText = [
        account.name,
        account.legalName,
        account.documentNumber,
        account.planName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [accounts, searchTerm]);

  const isAccountSwitchMode =
    new URLSearchParams(location.search).get("modo") === "trocar" ||
    location.state?.mode === "switch-account";

  useEffect(() => {
    if (
      isAccountSwitchMode ||
      loading ||
      refreshing ||
      user?.accountId ||
      accounts.length !== 1
    ) {
      return;
    }

    selectAccount(accounts[0].id);
  }, [
    accounts,
    isAccountSwitchMode,
    loading,
    refreshing,
    selectAccount,
    user?.accountId,
  ]);

  async function handleLogout() {
    await logout();
    navigate(authRoutes.login, { replace: true });
  }

  if (!isAccountSwitchMode && !user?.accountSelectionRequired && user?.accountId) {
    return <Navigate to={authRoutes.dashboard} replace />;
  }

  if (String(user?.emailVerified).toLowerCase() !== "true") {
    return <Navigate to={authRoutes.verifyEmail} replace />;
  }

  const shouldShowDirectAccessTransition =
    !isAccountSwitchMode &&
    !user?.accountId &&
    !error &&
    (loading || refreshing || accounts.length === 1);

  if (shouldShowDirectAccessTransition) {
    return <DirectAccountTransition />;
  }

  const visibleCount = filteredAccounts.length;

  return (
    <Box
      sx={(theme) => ({
        bgcolor: theme.palette.background.default,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
      })}
    >
      <Box
        component="header"
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexShrink: 0,
          height: { xs: APP_SHELL.mobileHeaderHeight, md: APP_SHELL.topbarHeight },
          justifyContent: "space-between",
          px: { xs: 2, sm: 3, md: 5 },
        })}
      >
        <Box
          component="img"
          src={logoMainDark123Zap}
          alt="123zap"
          sx={{
            display: "block",
            height: { xs: 28, md: 32 },
            maxWidth: { xs: 116, md: 132 },
            objectFit: "contain",
            objectPosition: "left center",
            width: "auto",
          }}
        />

        <Stack alignItems="center" direction="row" spacing={1}>
          <TopbarIconAction
            label="Documentação"
            onClick={openDocumentation}
          >
            <QuestionMarkCircleIcon aria-hidden height={22} width={22} />
          </TopbarIconAction>

          <SelectionAccountMenu onLogout={handleLogout} user={user} />
        </Stack>
      </Box>

      <Box
        sx={(theme) => ({
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 2.5, md: 2.75 },
        })}
      >
        <Typography
          component="p"
          sx={(theme) => ({
            color: theme.palette.primary.main,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.35,
            mb: 1.1,
          })}
        >
          Contas
        </Typography>

        <Typography
          component="h1"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            fontSize: { xs: 28, sm: 32 },
            fontWeight: 700,
            letterSpacing: 0,
            lineHeight: 1.08,
          })}
        >
          Selecione uma conta
        </Typography>

        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: 1.35,
            mt: 0.45,
          })}
        >
          {loading ? "Carregando contas..." : accountsLabel}
        </Typography>

        <TextField
          disabled={loading || refreshing}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Pesquisar"
          size="small"
          value={searchTerm}
          sx={(theme) => ({
            mt: 2,
            width: "100%",
            "& .MuiInputBase-root": {
              bgcolor: theme.palette.background.paper,
              borderRadius: "6px",
              height: 43,
              pr: 0.75,
            },
            "& input": {
              fontSize: 14,
            },
          })}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        component="main"
        sx={(theme) => ({
          bgcolor: theme.palette.surface.muted,
          flex: 1,
          px: { xs: 1.5, sm: 3, md: 5 },
          py: { xs: 2, md: 2.5 },
        })}
      >
        {error && (
          <Box
            sx={(theme) => ({
              bgcolor: theme.palette.feedback.error.surface,
              border: `1px solid ${theme.palette.feedback.error.border}`,
              borderRadius: 1,
              color: theme.palette.feedback.error.text,
              mb: 1.5,
              px: 2,
              py: 1.25,
            })}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        )}

        <Stack spacing={1}>
          {loading ? (
            <AccountsSkeleton />
          ) : hasAccounts && filteredAccounts.length ? (
            filteredAccounts.map((account) => (
              <AccountRow
                account={account}
                disabled={refreshing}
                key={account.id}
                loading={loadingAccountId === account.id}
                onAccess={selectAccount}
                onSelect={setSelectedCardId}
                selected={selectedCardId === account.id}
              />
            ))
          ) : (
            <EmptyAccounts
              description={
                hasAccounts
                  ? "Tente ajustar a pesquisa para localizar a conta desejada."
                  : "Não encontramos contas ativas vinculadas ao seu usuário."
              }
              onReload={reload}
              showReload={!hasAccounts}
              title={hasAccounts ? "Nenhuma conta encontrada" : "Nenhuma conta disponível"}
            />
          )}
        </Stack>
      </Box>

      <Box
        component="footer"
        sx={(theme) => ({
          alignItems: "center",
          bgcolor: theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexShrink: 0,
          gap: 1.5,
          height: 58,
          justifyContent: "center",
        })}
      >
        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: 13,
            fontWeight: 500,
            minWidth: 72,
            textAlign: "right",
          })}
        >
          {visibleCount ? `1-${visibleCount} de ${visibleCount}` : "0 de 0"}
        </Typography>
        <IconButton aria-label="Página anterior" disabled size="small">
          <KeyboardArrowLeft />
        </IconButton>
        <IconButton aria-label="Próxima página" disabled size="small">
          <KeyboardArrowRight />
        </IconButton>
      </Box>
    </Box>
  );
}

export function DirectAccountTransition({
  showVariantLabel = false,
  variant = "three-dots-sync",
}) {
  const option = resolveAccessTransitionOption(variant);
  const animateLogo = option.id === "logo-breath";

  return (
    <Box
      sx={(theme) => ({
        alignItems: "center",
        bgcolor: theme.palette.surface.default,
        display: "flex",
        minHeight: "100vh",
        px: 3,
      })}
    >
      <Box
        sx={{
          mx: "auto",
          textAlign: "center",
          width: "min(100%, 360px)",
        }}
      >
        <Box
          component="img"
          src={logoMainDark123Zap}
          alt="123zap"
          sx={(theme) => ({
            animation: animateLogo
              ? "accountLogoBreath 1.6s ease-in-out infinite"
              : "none",
            display: "block",
            height: 36,
            mx: "auto",
            objectFit: "contain",
            width: "auto",
            "@keyframes accountLogoBreath": {
              "0%, 100%": {
                filter: "drop-shadow(0 0 0 rgba(139, 70, 255, 0))",
                opacity: 0.92,
                transform: "scale(1)",
              },
              "50%": {
                filter: `drop-shadow(0 0 16px ${theme.palette.primary.main}55)`,
                opacity: 1,
                transform: "scale(1.025)",
              },
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              filter: "none",
              transform: "none",
            },
          })}
        />

        <Typography
          component="h1"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            fontSize: 20,
            fontWeight: 700,
            lineHeight: 1.2,
            mt: 3,
          })}
        >
          Preparando seu acesso
        </Typography>

        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: 14,
            lineHeight: 1.45,
            mt: 0.75,
          })}
        >
          Carregando conta, permissões e ambiente.
        </Typography>

        <AccessTransitionLoader key={option.id} variant={option.id} />

        {showVariantLabel && (
          <Typography
            sx={(theme) => ({
              color: theme.palette.text.secondary,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.2,
              mt: 2.2,
              textTransform: "uppercase",
            })}
          >
            {option.label}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function AccountAccessTransitionPreview() {
  return <DirectAccountTransition variant="three-dots-sync" />;
}

function AccessTransitionLoader({ variant }) {
  if (variant === "orbit-pulse") return <OrbitPulseLoader />;
  if (variant === "three-dots-sync") return <ThreeDotsSyncLoader />;
  if (variant === "access-beam") return <AccessBeamLoader />;
  if (variant === "logo-breath") return <LogoBreathLoader />;
  return <SignalScanLoader />;
}

function SignalScanLoader() {
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        height: 44,
        mt: 3.1,
        mx: "auto",
        position: "relative",
        width: 180,
        "&::before": {
          background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}44, transparent)`,
          borderRadius: 999,
          content: '""',
          height: 1,
          left: 0,
          position: "absolute",
          right: 0,
          top: 21,
        },
        "&::after": {
          animation: "accountSignalScan 1.4s cubic-bezier(.55,0,.1,1) infinite",
          background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, ${theme.palette.primary.light}, transparent)`,
          borderRadius: 999,
          boxShadow: `0 0 18px ${theme.palette.primary.main}55`,
          content: '""',
          height: 2,
          left: 0,
          position: "absolute",
          top: 20.5,
          width: 54,
        },
        "@keyframes accountSignalScan": {
          "0%": { opacity: 0, transform: "translateX(-32px) scaleX(.65)" },
          "18%": { opacity: 1 },
          "55%": { opacity: 1, transform: "translateX(82px) scaleX(1)" },
          "100%": { opacity: 0, transform: "translateX(158px) scaleX(.55)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "&::after": {
            animation: "none",
            left: 63,
            opacity: 1,
          },
        },
      })}
    />
  );
}

function OrbitPulseLoader() {
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        border: `1px solid ${theme.palette.primary.main}22`,
        borderRadius: "50%",
        height: 52,
        mt: 3,
        mx: "auto",
        position: "relative",
        width: 52,
        "&::before": {
          animation: "accountOrbit 1.5s linear infinite",
          borderRadius: "50%",
          content: '""',
          height: "100%",
          inset: -1,
          position: "absolute",
          width: "100%",
          background: `conic-gradient(from 90deg, transparent 0deg, transparent 245deg, ${theme.palette.primary.main} 320deg, transparent 360deg)`,
          WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
          mask: "radial-gradient(farthest-side, transparent calc(100% - 2px), #000 calc(100% - 2px))",
        },
        "&::after": {
          animation: "accountOrbitCore 1.5s ease-in-out infinite",
          bgcolor: theme.palette.primary.main,
          borderRadius: "50%",
          boxShadow: `0 0 18px ${theme.palette.primary.main}66`,
          content: '""',
          height: 8,
          left: "calc(50% - 4px)",
          position: "absolute",
          top: "calc(50% - 4px)",
          width: 8,
        },
        "@keyframes accountOrbit": {
          to: { transform: "rotate(360deg)" },
        },
        "@keyframes accountOrbitCore": {
          "0%, 100%": { opacity: 0.65, transform: "scale(.85)" },
          "50%": { opacity: 1, transform: "scale(1.25)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "&::before, &::after": {
            animation: "none",
          },
        },
      })}
    />
  );
}

function ThreeDotsSyncLoader() {
  return (
    <Stack
      aria-hidden
      direction="row"
      spacing={1}
      sx={{
        alignItems: "center",
        height: 44,
        justifyContent: "center",
        mt: 3.1,
      }}
    >
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={(theme) => ({
            animation: "accountDotSync 1.2s ease-in-out infinite",
            animationDelay: `${index * 140}ms`,
            bgcolor: theme.palette.primary.main,
            borderRadius: "50%",
            height: 8,
            opacity: 0.28,
            width: 8,
            "@keyframes accountDotSync": {
              "0%, 100%": { opacity: 0.28, transform: "translateY(0) scale(.9)" },
              "42%": { opacity: 1, transform: "translateY(-4px) scale(1.1)" },
            },
            "@media (prefers-reduced-motion: reduce)": {
              animation: "none",
              opacity: 0.7,
              transform: "none",
            },
          })}
        />
      ))}
    </Stack>
  );
}

function AccessBeamLoader() {
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        height: 54,
        mt: 2.8,
        mx: "auto",
        position: "relative",
        width: 76,
        "&::before": {
          border: `1px solid ${theme.palette.primary.main}24`,
          borderRadius: "18px",
          content: '""',
          inset: "4px 18px",
          position: "absolute",
        },
        "&::after": {
          animation: "accountAccessBeam 1.4s ease-in-out infinite",
          background: `linear-gradient(180deg, transparent, ${theme.palette.primary.main}, transparent)`,
          borderRadius: 999,
          boxShadow: `0 0 20px ${theme.palette.primary.main}66`,
          content: '""',
          height: 42,
          left: "calc(50% - 1px)",
          position: "absolute",
          top: 6,
          width: 2,
        },
        "@keyframes accountAccessBeam": {
          "0%, 100%": { opacity: 0.35, transform: "scaleY(.55)" },
          "45%": { opacity: 1, transform: "scaleY(1)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "&::after": {
            animation: "none",
            opacity: 0.8,
            transform: "none",
          },
        },
      })}
    />
  );
}

function LogoBreathLoader() {
  return (
    <Box
      aria-hidden
      sx={(theme) => ({
        height: 44,
        mt: 3.1,
        mx: "auto",
        position: "relative",
        width: 96,
        "&::before, &::after": {
          animation: "accountBrandHalo 1.6s ease-in-out infinite",
          border: `1px solid ${theme.palette.primary.main}33`,
          borderRadius: "50%",
          content: '""',
          height: 34,
          left: "calc(50% - 17px)",
          position: "absolute",
          top: 5,
          width: 34,
        },
        "&::after": {
          animationDelay: "220ms",
          height: 22,
          left: "calc(50% - 11px)",
          top: 11,
          width: 22,
        },
        "@keyframes accountBrandHalo": {
          "0%, 100%": { opacity: 0.12, transform: "scale(.72)" },
          "50%": { opacity: 0.65, transform: "scale(1.15)" },
        },
        "@media (prefers-reduced-motion: reduce)": {
          "&::before, &::after": {
            animation: "none",
            opacity: 0.4,
            transform: "none",
          },
        },
      })}
    />
  );
}

function SelectionAccountMenu({ onLogout, user }) {
  const userName = user?.name || user?.username;
  const initial = (userName || user?.email || "Z").trim().charAt(0).toUpperCase();
  const avatarUrl = buildUserAvatarUrl(user);
  const triggerImageUrl = user?.accountLogoUrl || avatarUrl;

  return (
    <ActionMenu
      ariaLabel="Abrir opções da conta"
      menuHeader={
        <AccountMenuHeader
          avatarUrl={avatarUrl}
          email={user?.email}
          initial={initial}
          productName={user?.accountName || "123zap"}
          userName={userName}
        />
      }
      menuItemSx={ACCOUNT_MENU_ITEM_SX}
      menuListSx={ACCOUNT_MENU_LIST_SX}
      menuPaperSx={ACCOUNT_MENU_PAPER_SX}
      minWidth={ACCOUNT_MENU_WIDTH}
      placement="bottom-end"
      actions={[
        {
          id: "logout",
          icon: <LogoutIcon aria-hidden height={24} width={24} />,
          iconPosition: "start",
          label: "Sair",
          onClick: onLogout,
        },
      ]}
      renderTrigger={({ buttonProps }) => (
        <TopbarIconAction
          controls={buttonProps["aria-controls"]}
          expanded={buttonProps["aria-expanded"]}
          hasPopup={buttonProps["aria-haspopup"]}
          label="Abrir opções da conta"
          onClick={buttonProps.onClick}
        >
          <Box
            aria-hidden
            sx={(theme) => ({
              alignItems: "center",
              bgcolor: theme.palette.action.selected,
              borderRadius: "50%",
              color: theme.palette.primary.main,
              display: "flex",
              fontSize: 12,
              fontWeight: 700,
              height: 24,
              justifyContent: "center",
              overflow: "hidden",
              width: 24,
            })}
          >
            {triggerImageUrl ? (
              <Box
                component="img"
                src={triggerImageUrl}
                alt=""
                sx={{ height: "100%", objectFit: "cover", width: "100%" }}
              />
            ) : (
              initial
            )}
          </Box>
        </TopbarIconAction>
      )}
    />
  );
}

function AccountRow({
  account,
  disabled,
  loading,
  onAccess,
  onSelect,
  selected,
}) {
  const accountDescription = getAccountDescription(account);
  const accountDisplayName = getAccountDisplayName(account);
  const accountInitialsSource = getAccountInitialsSource(account);
  const handleSelect = () => {
    if (!disabled) onSelect(account.id);
  };
  const handleAccess = (event) => {
    event.stopPropagation();
    if (!disabled) onAccess(account.id);
  };

  return (
    <Box
      component="div"
      onClick={handleSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleSelect();
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      sx={(theme) => ({
        alignItems: "center",
        bgcolor:
          loading || selected
            ? theme.palette.action.selected
            : theme.palette.background.paper,
        border: `1px solid ${
          selected ? theme.palette.primary.main : "transparent"
        }`,
        borderRadius: "8px",
        color: "inherit",
        cursor: disabled ? "default" : "pointer",
        display: "grid",
        gap: { xs: 1.5, md: 2 },
        gridTemplateColumns: {
          xs: "48px minmax(0, 1fr)",
          sm: "48px minmax(0, 1fr) auto",
        },
        minHeight: { xs: 112, sm: 72 },
        opacity: disabled && !loading ? 0.72 : 1,
        px: { xs: 1.5, md: 2 },
        py: 1,
        textAlign: "left",
        transition: "background-color 120ms ease, border-color 120ms ease, opacity 120ms ease",
        width: "100%",
        "&:focus-visible": {
          boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
          outline: "none",
        },
        "&:hover": {
          bgcolor: disabled ? theme.palette.action.selected : theme.palette.action.hover,
        },
      })}
    >
      <Avatar
        src={account.logoUrl || undefined}
        sx={(theme) => ({
          bgcolor: theme.palette.surface.inverse,
          color: theme.palette.background.paper,
          fontSize: 16,
          fontWeight: 800,
          height: 48,
          width: 48,
        })}
      >
        {account.logoUrl ? null : getInitials(accountInitialsSource)}
      </Avatar>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={(theme) => ({
            color: theme.palette.text.primary,
            fontSize: 18,
            fontWeight: 800,
            lineHeight: 1.2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {accountDisplayName}
        </Typography>
        <Typography
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: 14,
            lineHeight: 1.35,
            mt: 0.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {accountDescription}
        </Typography>
      </Box>

      <Button
        disabled={disabled}
        onClick={handleAccess}
        size="small"
        sx={{
          borderRadius: 999,
          display: "inline-flex",
          fontSize: 13,
          height: 40,
          gridColumn: { xs: "1 / -1", sm: "auto" },
          justifySelf: "end",
          minWidth: { xs: 112, sm: 84 },
          px: 2,
        }}
        variant="contained"
      >
        {loading ? <CircularProgress color="inherit" size={16} /> : "Acessar"}
      </Button>
    </Box>
  );
}

function AccountsSkeleton() {
  return Array.from({ length: 2 }).map((_, index) => (
    <Stack
      alignItems="center"
      direction="row"
      key={index}
      spacing={2}
      sx={(theme) => ({
        bgcolor: theme.palette.background.paper,
        borderRadius: "8px",
        minHeight: 72,
        px: { xs: 1.5, md: 2 },
        py: 1,
      })}
    >
      <Skeleton height={48} variant="circular" width={48} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton height={22} width="26%" />
        <Skeleton height={18} width="38%" />
      </Box>
      <Skeleton
        height={40}
        sx={{ borderRadius: 999 }}
        width={84}
      />
    </Stack>
  ));
}

function EmptyAccounts({ description, onReload, showReload, title }) {
  return (
    <Stack
      alignItems="center"
      spacing={1.5}
      sx={(theme) => ({
        bgcolor: theme.palette.background.paper,
        borderRadius: "8px",
        py: 8,
        textAlign: "center",
      })}
    >
      <Business color="disabled" sx={{ fontSize: 44 }} />
      <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography color="text.secondary" sx={{ fontSize: 14, maxWidth: 420 }}>
        {description}
      </Typography>
      {showReload && (
        <Button onClick={onReload} size="small" variant="outlined">
          Tentar novamente
        </Button>
      )}
    </Stack>
  );
}
